import React, { forwardRef, useState } from 'react';
import { DateRange, Matcher, SelectRangeEventHandler } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import LucideIcon from '../Icons/LucideIcon';
import Typo from '../Typo';
import { cn } from '@/src/utils';
import LocalIcon from '../Icons/LocalIcon';
import ClearSvg from '@/src/assets/icons/icon-input-clear.svg';

const datePickerSizeData = {
  medium: 'px-[12px] py-[8px] h-[40px]',
  small: 'px-[12px] py-[6px] h-[32px]',
};

export interface DateRangePickerProps {
  value?: DateRange;
  onChange?: SelectRangeEventHandler;
  dateFormat?: string;
  triggerProps?: React.ComponentPropsWithoutRef<
    typeof PopoverPrimitive.Trigger
  >;
  contentProps?: React.ComponentPropsWithoutRef<
    typeof PopoverPrimitive.Content
  >;
  size?: keyof typeof datePickerSizeData;
  allowClear?: boolean;
  placeholder?: string;
  dateDisabledMatcher?: Matcher;
  defaultMonth?: Date;
}

const DateRangePicker = forwardRef<HTMLButtonElement, DateRangePickerProps>(
  (
    {
      value,
      onChange,
      dateFormat = 'YYYY-MM-DD',
      size = 'medium',
      triggerProps,
      contentProps,
      allowClear = true,
      placeholder,
      dateDisabledMatcher,
      defaultMonth,
    },
    ref,
  ) => {
    const [isHover, setIsHover] = useState(false);

    const disabledTriggerStyle = triggerProps?.disabled
      ? 'bg-gray1 hover:bg-gray1 border-gray3 text-gray6 !pointer-events-auto cursor-not-allowed'
      : '';

    const renderRightIcon = () => {
      if (allowClear && isHover && value) {
        return (
          <LocalIcon
            iconSrc={ClearSvg}
            iconSize={16}
            onClick={e => {
              onChange?.(undefined, undefined as any, {}, e);
            }}
            color={triggerProps?.disabled ? 'gray6' : 'gray9'}
            className="flex flex-shrink-0"
          />
        );
      }

      return (
        <LucideIcon
          size={16}
          name="Calendar"
          color={triggerProps?.disabled ? 'gray6' : 'gray9'}
          className="flex flex-shrink-0"
        />
      );
    };

    return (
      <Popover>
        <PopoverTrigger
          asChild
          ref={ref}
          className={cn(
            'border bg-white px-4 hover:bg-white focus-visible:border-gray9 focus-visible:ring-0 focus-visible:ring-offset-0',
            disabledTriggerStyle,
            triggerProps?.className,
          )}
          onMouseOver={() => {
            setIsHover(true);
          }}
          onMouseLeave={() => {
            setIsHover(false);
          }}
          {...triggerProps}
        >
          <Button
            id="date"
            variant="outline"
            className={cn(
              `w-[100%] items-center justify-between ${datePickerSizeData[size]} overflow-hidden disabled:opacity-100`,
              !value && 'text-muted-foreground',
            )}
          >
            {value?.from ? (
              value.to ? (
                <Typo typoType="b14r">
                  {dayjs(value.from).format(dateFormat)} -{' '}
                  {dayjs(value.to).format(dateFormat)}
                </Typo>
              ) : (
                dayjs(value.from).format(dateFormat)
              )
            ) : (
              <Typo typoType="b14r" typoColor="gray4">
                {placeholder ?? 'YYYY-MM-DD ~ YYYY-MM-DD'}
              </Typo>
            )}

            {renderRightIcon()}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start" {...contentProps}>
          <Calendar
            initialFocus
            disabled={dateDisabledMatcher}
            mode="range"
            defaultMonth={value?.from || defaultMonth}
            selected={value}
            onSelect={onChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

export default DateRangePicker;
